import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MagicWand01 } from '../../../../assets';
import { ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { theme } from '../../../../theme';

export interface WidgetCardProps {
  isLoading: boolean;
  isSuccessful: boolean;
  title?: string;
  value?: string | number;
  progressPercentage?: number;
  changeValue?: number;
  usesProgressBar?: boolean;
  tooltipText?: string;
}

const WidgetCard: React.FC<WidgetCardProps> = ({
  isLoading,
  isSuccessful,
  title,
  value,
  progressPercentage,
  changeValue,
  usesProgressBar,
  tooltipText,
}) => {
  const { t } = useTranslation('common');

  const renderChangeInPercentage = () => {
    return (
      <Typography
        sx={{
          fontSize: 14,
          color: renderChangeInPercentageColor(),
          display: 'flex',
          alignItems: 'center',
          minHeight: 24,
        }}
      >
        {changeValue}%
        {renderChangeInPercentageArrow()}
      </Typography>
    );
  };

  const renderChangeInPercentageColor = () => {
    if (changeValue === 0) {
      return theme.palette.custom.gray[500];
    } else if (changeValue && changeValue > 0) {
      return theme.palette.error[500];
    } else {
      return theme.palette.success[500];
    }
  };

  const renderChangeInPercentageArrow = () => {
    if (changeValue === 0) {
      return <ArrowForward fontSize="small" />;
    } else if (changeValue && changeValue > 0) {
      return <ArrowUpwardIcon fontSize="small" />;
    } else if (changeValue && changeValue < 0) {
      return <ArrowDownwardIcon fontSize="small" />;
    }
  };

  const renderFetchingErrorAlert = (): React.ReactNode => {
    return (
      <Alert severity="error">
        {t('reporting.widget-card.fetch-error')}
      </Alert>
    );
  };

  const renderWidgetCardContent = (): React.ReactNode => {
    return (
      <>
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            width: '80px',
            height: '80px',
            flexShrink: 0,
          }}
        >
          {usesProgressBar && progressPercentage !== undefined && progressPercentage !== null ? (
            <>
              <CircularProgress
                variant="determinate"
                value={100}
                size={80}
                thickness={5}
                sx={{
                  color:
                    progressPercentage >= 0
                      ? theme.palette.success[700]
                      : theme.palette.error[500],
                  position: 'absolute',
                }}
              />
              <CircularProgress
                variant="determinate"
                value={progressPercentage > 100 ? 100 : progressPercentage}
                size={80}
                thickness={5}
                sx={{ color: theme.palette.custom.gray[300] }}
              />
            </>
          ) : (
            <Box>
              <HighlightOffIcon color="disabled"
                sx={{ fontSize: 40 }} />
              <Typography color="text.secondary">
                {t('reporting.widget-card.no-progress')}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            marginLeft: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box display="flex"
            alignItems="flex-start">
            <Box sx={{ marginTop: '2px', marginRight: '6px' }}>
              <MagicWand01 fontSize="small" />
            </Box>
            <Typography sx={{ maxWidth: 120 }}
              fontWeight="bold">
              {title}
            </Typography>
          </Box>

          <Typography
            color="text.secondary"
            sx={{
              fontSize: 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'center',
            }}
          >
            {t('reporting.widget-card.last-weeks')}
          </Typography>

          <Box display="flex"
            alignItems="baseline">
            <Typography fontWeight="bold"
              sx={{ marginRight: 1, minWidth: 80 }}>
              {value}
            </Typography>
            {changeValue !== undefined &&
              changeValue !== null &&
              renderChangeInPercentage()}
          </Box>
        </Box>
      </>
    );
  };

  const renderAlertOrContent = (): React.ReactNode => {
    return isSuccessful ? renderWidgetCardContent() : renderFetchingErrorAlert();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        boxShadow: 1,
        borderRadius: 2,
        backgroundColor: theme.palette.common.white,
        width: 260,
        height: 150,
        margin: 1,
      }}
    >
      {tooltipText && (
        <Tooltip title={tooltipText}
          placement="top">
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 2,
            }}
          >
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      {isLoading ? <LoadingWheel /> : renderAlertOrContent()}
    </Box>
  );
};

export default WidgetCard;
